import { useEffect, useRef, useState } from 'react'
import { MessageBubble } from './MessageBubble'
import { ToolConfirmationBubble } from './ToolConfirmationBubble'
import { CompressionMessage } from './CompressionMessage'
import { Message, useMessageStore } from '../../stores/messageStore'
import { messageService } from '../../services/messageService'
import { useMessage } from '../../hooks/useMessage'
import { ToolConfirmationOutcome } from '../../../../shared-types/dist/index.js'
import { cn } from '@/lib/utils'

interface MessageListProps {
  messages: Message[]
  conversationId: string
}

export function MessageList({ messages, conversationId }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [autoScroll] = useState(true)
  const { sendMessage } = useMessage()
  const { 
    getPendingConfirmations,
    isConversationCompressing
  } = useMessageStore()
  
  const pendingConfirmations = getPendingConfirmations()
  const isCompact = false // Can be made dynamic based on viewport

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: smooth ? 'smooth' : 'auto',
      block: 'end'
    })
  }

  // Note: Scroll handling is done by parent ChatArea component
  // This component just displays messages

  // Track previous message count to detect new messages vs content updates
  const prevMessageCountRef = useRef(messages.length)

  // Auto-scroll when NEW messages arrive (not on content updates during streaming)
  // This prevents jittery scrolling when large content is streamed in quickly
  useEffect(() => {
    const newMessageAdded = messages.length > prevMessageCountRef.current
    prevMessageCountRef.current = messages.length

    if (autoScroll && newMessageAdded) {
      scrollToBottom()
    }
  }, [messages.length, autoScroll])

  // Scroll to bottom on initial load
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(false), 100)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length > 0])

  // Keyboard shortcut: Ctrl/Cmd + End to scroll to bottom
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'End') {
        e.preventDefault()
        scrollToBottom()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  if (messages.length === 0) {
    return (
      <div className="relative flex-1 h-full">
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Start a conversation</h3>
            <p className="text-sm text-muted-foreground">
              Ask a question or share what's on your mind
            </p>
          </div>
        </div>
      </div>
    )
  }

  const handleConfirmation = async (toolCallId: string, outcome: ToolConfirmationOutcome) => {
    try {
      await messageService.sendToolConfirmation(conversationId, toolCallId, outcome)
    } catch (error) {
      console.error('Failed to send confirmation:', error)
    }
  }

  const handleRetry = async (retryConversationId: string, originalUserMessage: string) => {
    try {
      await sendMessage({
        conversationId: retryConversationId || conversationId,
        content: originalUserMessage,
        model: 'claude-3-5-sonnet-20241022'
      })
    } catch (error) {
      console.error('Failed to retry message:', error)
    }
  }

  return (
    <div className="relative">

        {/* Compression Message */}
        {isConversationCompressing(conversationId) && (
          <div className={cn(
            "border-b bg-muted/30",
            isCompact ? "py-1.5 px-2.5" : "py-2 px-3"
          )}>
            <CompressionMessage compressionInfo={{} as any} />
          </div>
        )}

        {/* Pending Confirmations */}
        {pendingConfirmations.map((confirmation) => (
          <div
            key={confirmation.toolCallId}
            className={cn(
              "border-b bg-yellow-50/50 dark:bg-yellow-900/10",
              isCompact ? "py-1.5 px-2.5" : "py-2 px-3"
            )}
          >
            <ToolConfirmationBubble
              confirmationDetails={confirmation.details}
              onConfirm={(outcome) => handleConfirmation(confirmation.toolCallId, outcome)}
            />
          </div>
        ))}

        {/* Messages */}
        <div className="space-y-3 py-3">
          {messages.map((message, index) => {
            const previousMessage = index > 0 ? messages[index - 1] : null
            const previousUserMessage = previousMessage?.role === 'user' ? previousMessage.content : undefined

            return (
              <MessageBubble
                key={message.id}
                message={message}
                onRetry={handleRetry}
                previousUserMessage={previousUserMessage}
              />
            )
          })}
        </div>

        {/* Scroll anchor */}
        <div ref={messagesEndRef} className="h-3" />

    </div>
  )
}